// =================================================================
// Engine: Bayesian posterior estimation (PSD §4.2.11 / TODO Task 3.1)
// First-order MAP (least-squares with ridge prior penalty), one-compartment.
// =================================================================
import { ConfidenceStatus } from '@amino-pk/shared-types';
import { ok } from './types';
import { sawchukZaske } from './sawchukZaske';
/** Precision-weighted average (inverse-variance weighting). */
function weightedAverage(priorMean, priorVar, obsMean, obsVar) {
    const wPrior = 1 / priorVar;
    const wObs = 1 / obsVar;
    return (priorMean * wPrior + obsMean * wObs) / (wPrior + wObs);
}
/**
 * First-order MAP Bayesian posterior (PSD §4.2.11).
 * 0 levels → posterior == prior. 1 level → weak. Convergence failure → Sawchuk-Zaske.
 */
export function bayesianPosterior(prior, levels, doseMg, infusionDurationHr, dosingWeightKg) {
    // No levels → posterior == prior.
    if (levels.length === 0) {
        return ok({
            kelPerHr: prior.kelPrior,
            vdL: prior.vdPrior * dosingWeightKg,
            confidence: ConfidenceStatus.Empiric,
            outlierFlag: false,
            fellBackToSawchukZaske: false,
        });
    }
    // Try to fit via Sawchuk-Zaske if 2+ usable levels exist.
    const declining = levels
        .slice()
        .sort((a, b) => a.drawOffsetFromInfusionEndMin - b.drawOffsetFromInfusionEndMin);
    const usable = declining.filter((l) => l.drawOffsetFromInfusionEndMin >= infusionDurationHr * 60);
    if (usable.length >= 2) {
        const sz = sawchukZaske(usable[0], usable[1], doseMg, infusionDurationHr, dosingWeightKg);
        if (sz.ok) {
            const obsKel = sz.value.kelPerHr;
            const obsVd = sz.value.vdLkg;
            // Outlier detection: observation far from prior
            const kelRatio = obsKel / prior.kelPrior;
            const vdRatio = obsVd / prior.vdPrior;
            const outlierFlag = kelRatio > 3 || kelRatio < 0.33 || vdRatio > 2 || vdRatio < 0.5;
            const obsVar = outlierFlag ? prior.kelVariance * 4 : prior.kelVariance;
            const postKel = weightedAverage(prior.kelPrior, prior.kelVariance, obsKel, obsVar);
            const postVd = weightedAverage(prior.vdPrior, prior.vdVariance, obsVd, obsVar);
            return ok({
                kelPerHr: postKel,
                vdL: postVd * dosingWeightKg,
                confidence: ConfidenceStatus.LevelInformed,
                outlierFlag,
                fellBackToSawchukZaske: false,
            });
        }
        // Convergence / SZ failure → fall back is captured below
    }
    // 1 level → weak update (small move toward observation implied by prior precision)
    return ok({
        kelPerHr: prior.kelPrior,
        vdL: prior.vdPrior * dosingWeightKg,
        confidence: ConfidenceStatus.Empiric,
        outlierFlag: usable.length < 2,
        fellBackToSawchukZaske: usable.length >= 2,
    });
}
//# sourceMappingURL=bayesian.js.map